import { getSupabaseClient } from './supabase.client.js';

/**
 * Repositorio de `pagos` (S5a, design.md AD8/AD13/AD18) para el panel de
 * staff. SOLO LECTURA desde el navegador: la tabla es append-only, con
 * únicamente una política de SELECT admin (`pagos_admin_read`, migración 29)
 * y SIN política de INSERT/UPDATE/DELETE. Los únicos escritores son las RPCs
 * `registrar_pago`/`anular_pago` (SECURITY DEFINER, `revoke ... from public,
 * anon, authenticated` + `grant to service_role`) — este repositorio, que
 * corre con la anon key del navegador, no tiene grant para escribir la tabla
 * directo; la escritura real vive en `api/payroll-movimientos.js`
 * (service_role).
 *
 * UN solo select batcheado, filtrado por `liquidacion_id in (...)`
 * (tasks.md S5a.4: "no view, no per-settlement read") — el conjunto de ids se
 * amplía a las anuladas del período en S5b (AD18); acá el caller decide qué
 * ids pedir. Sin `liquidacionIds` (o con un array vacío), devuelve `[]` SIN
 * consultar — a diferencia de `adelantos.repository.js`'s `listAdelantos`
 * (donde "sin filtro" es una lectura legítima de TODOS los adelantos), acá
 * "sin ids" no tiene un caller legítimo: nadie necesita jamás leer la tabla
 * `pagos` completa, así que degradar a una lectura sin filtro sería un
 * full-table read accidental de un ledger de dinero.
 *
 * Columnas SIEMPRE explícitas (nunca `select('*')`), mismo patrón que
 * `adelantos.repository.js`/`liquidaciones.repository.js`.
 */

const PAGO_COLUMNS = 'id, liquidacion_id, fecha, medio, monto, nota, creado_por, creado_at, anulado_at, anulado_por, anulado_motivo';

/**
 * Re-lanza un error de Supabase preservando el código de Postgres (mismo
 * patrón que el resto de los repositorios de este panel).
 * @param {{ message: string, code?: string }} error
 * @returns {never}
 */
function rethrow(error) {
    const err = new Error(error.message);
    if (error.code) err.code = error.code;
    throw err;
}

/**
 * Pagos (activos y anulados) de un conjunto de liquidaciones, fecha
 * descendente. El parámetro `liquidacionIds` filtra por liquidación: las
 * filas devueltas pueden pertenecer tanto a liquidaciones vigentes
 * (`confirmada`) como a liquidaciones anuladas — es el llamador quien decide
 * qué ids pedir según lo que necesite mostrar (la pill de estado de pago
 * necesita las liquidaciones confirmadas del período; el aviso de pagos
 * previos, design.md AD18, necesita además las anuladas).
 *
 * Si Supabase no está configurado devuelve `[]` (misma degradación que
 * `listAdelantos`/`listFichadas`/`listEmpleados`).
 * @param {{ liquidacionIds?: string[] }} [params]
 * @returns {Promise<Array<object>>}
 * @throws {Error} si Supabase responde con error
 */
export async function listPagos({ liquidacionIds } = {}) {
    if (!Array.isArray(liquidacionIds) || liquidacionIds.length === 0) return [];

    const client = getSupabaseClient();
    if (!client) return [];

    const { data, error } = await client
        .from('pagos')
        .select(PAGO_COLUMNS)
        .in('liquidacion_id', liquidacionIds)
        .order('fecha', { ascending: false });
    if (error) rethrow(error);
    return data ?? [];
}
